import { useRef, useCallback } from 'react';

/**
 * Circuit Breaker Hook - Prevents Infinite Reconnection Loops
 *
 * Detects and prevents rapid reconnection attempts that could indicate:
 * - Infinite loops in React useEffect dependencies
 * - Server-side issues causing immediate disconnections
 * - Client-side bugs triggering repeated connections
 *
 * Usage:
 * const circuitBreaker = useConnectionCircuitBreaker({
 *   maxAttempts: 5,
 *   timeWindow: 10000,
 *   cooldownPeriod: 30000
 * });
 *
 * // Before attempting connection:
 * if (!circuitBreaker.canAttempt()) {
 *   console.error('Circuit breaker open - too many connection attempts');
 *   return;
 * }
 *
 * // After connection attempt:
 * circuitBreaker.recordAttempt();
 *
 * // On successful stable connection (e.g., after 5 seconds):
 * circuitBreaker.reset();
 */
export const useConnectionCircuitBreaker = (options = {}) => {
  const {
    maxAttempts = 5,           // Max connection attempts in time window
    timeWindow = 10000,        // Time window in ms (10 seconds)
    cooldownPeriod = 30000,    // Cooldown period in ms (30 seconds)
    onCircuitOpen = null,      // Callback when circuit opens
    onCircuitReset = null,     // Callback when circuit resets
  } = options;

  // Track connection attempts with timestamps
  const attemptsRef = useRef([]);

  // Track if circuit is open (blocking connections)
  const isOpenRef = useRef(false);

  // Track when circuit was opened
  const openedAtRef = useRef(null);

  /**
   * Check if a new connection attempt is allowed
   */
  const canAttempt = useCallback(() => {
    const now = Date.now();

    // If circuit is open, check if cooldown period has elapsed
    if (isOpenRef.current) {
      const timeSinceOpened = now - openedAtRef.current;

      if (timeSinceOpened < cooldownPeriod) {
        const remainingTime = Math.ceil((cooldownPeriod - timeSinceOpened) / 1000);
        console.warn(
          `🚫 Circuit breaker is OPEN - connection attempts blocked for ${remainingTime} more seconds`
        );
        return false;
      } else {
        // Cooldown period elapsed, automatically reset circuit
        console.log('✅ Circuit breaker cooldown elapsed - resetting to closed state');
        reset();
        return true;
      }
    }

    // Circuit is closed, allow attempt
    return true;
  }, [cooldownPeriod]);

  /**
   * Record a connection attempt
   */
  const recordAttempt = useCallback(() => {
    const now = Date.now();

    // Add current attempt
    attemptsRef.current.push(now);

    // Remove attempts outside the time window
    attemptsRef.current = attemptsRef.current.filter(
      timestamp => now - timestamp < timeWindow
    );

    // Check if we've exceeded max attempts in time window
    if (attemptsRef.current.length >= maxAttempts) {
      console.error(
        `⚠️ CIRCUIT BREAKER TRIGGERED: ${attemptsRef.current.length} connection attempts in ${timeWindow}ms`
      );
      console.error(
        `🔒 Blocking further connection attempts for ${cooldownPeriod / 1000} seconds`
      );

      // Open circuit
      isOpenRef.current = true;
      openedAtRef.current = now;

      // Call callback if provided
      if (onCircuitOpen) {
        onCircuitOpen({
          attempts: attemptsRef.current.length,
          timeWindow,
          cooldownPeriod,
        });
      }

      return false; // Attempt blocked
    }

    console.log(
      `📊 Connection attempt recorded (${attemptsRef.current.length}/${maxAttempts} in last ${timeWindow / 1000}s)`
    );
    return true; // Attempt allowed
  }, [maxAttempts, timeWindow, cooldownPeriod, onCircuitOpen]);

  /**
   * Reset circuit breaker (call after successful stable connection)
   */
  const reset = useCallback(() => {
    const wasOpen = isOpenRef.current;

    attemptsRef.current = [];
    isOpenRef.current = false;
    openedAtRef.current = null;

    if (wasOpen) {
      console.log('✅ Circuit breaker RESET - connection attempts allowed again');
      if (onCircuitReset) {
        onCircuitReset();
      }
    }
  }, [onCircuitReset]);

  /**
   * Get current circuit state
   */
  const getState = useCallback(() => {
    const now = Date.now();
    return {
      isOpen: isOpenRef.current,
      attempts: attemptsRef.current.length,
      maxAttempts,
      timeWindow,
      cooldownRemaining: isOpenRef.current
        ? Math.max(0, cooldownPeriod - (now - openedAtRef.current))
        : 0,
    };
  }, [maxAttempts, timeWindow, cooldownPeriod]);

  /**
   * Manually open circuit (for testing or emergency situations)
   */
  const forceOpen = useCallback(() => {
    console.warn('⚠️ Circuit breaker MANUALLY OPENED');
    isOpenRef.current = true;
    openedAtRef.current = Date.now();

    if (onCircuitOpen) {
      onCircuitOpen({
        attempts: attemptsRef.current.length,
        timeWindow,
        cooldownPeriod,
        manual: true,
      });
    }
  }, [timeWindow, cooldownPeriod, onCircuitOpen]);

  return {
    canAttempt,
    recordAttempt,
    reset,
    getState,
    forceOpen,
  };
};
